import { HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { BcmBookingsApiService } from '@bcmApiServices/bcm-bookings.api-service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { BcmBooking } from '@shared/models/bcm-booking';

import { RESERVATION_DEPOSIT_TITLE_KEY } from '../dtos/booking-v2.dtos';
import { BookingV2Facade, BookingV2Step } from './booking-v2.facade';
import { BookingV2RelationPricingService } from './booking-v2-relation-pricing.service';
import { buildBookingV2CommitDto, BookingV2CommitSnapshot, BookingV2DepositInput } from './booking-v2-payload.mapper';

// A required entry the user still has to provide before the booking can be saved,
// paired with the wizard step that fixes it (drives the save-blocker panel +
// stepper markers).
export interface BookingV2MissingRequirement {
    labelKey: string;
    step: BookingV2Step;
}

// Orchestrates saving the wizard. Step-1 scope: a single atomic POST of an
// EXISTING person + REAL existing boat to the legacy bookings endpoint (which
// references those entities by id). New person/boat (backend nested create)
// and advanced data are layered on in later increments — see
// project_booking_v2_commit. Provided at the dialog level so it shares the
// dialog's BookingV2Facade instance.
@Injectable()
export class BookingV2CommitService {

    private readonly facade = inject(BookingV2Facade);
    private readonly bookingsApi = inject(BcmBookingsApiService);
    private readonly notifications = inject(AppNotificationService);
    // Owns the combined (relation + services) season requirement; Save blocks on it.
    private readonly relationPricing = inject(BookingV2RelationPricingService);

    readonly saving = signal<boolean>(false);

    // The dimension-only 'manual' boat (properties mode) carries no real boat
    // data to persist — it can't be committed; the user must pick or create one.
    readonly blockedByManualBoat = computed<boolean>(() =>
        this.facade.selectedBoat()?.origin === 'manual',
    );

    // A subscription line without a billing interval would be silently dropped by
    // the mapper. The services tab already blocks creating one, so this is a
    // defensive guard against any other mutation path — it disables Save (and the
    // tooltip explains why) rather than letting an incomplete subscription vanish.
    readonly hasIncompleteSubscription = computed<boolean>(() =>
        this.facade.services().some(s => s.isSubscription && !s.subscriptionConfig?.payableOptionId),
    );

    // The still-missing required entries, in wizard order, each linked to the step
    // that fixes it. Save needs a complete date range, a person/company (existing
    // or new) and a committable boat (the berth is optional — a booking can hold a
    // boat with no berth; only the dimension-only 'manual' boat can't be saved).
    // The save-blocker panel + stepper markers render from this list.
    readonly missingRequirements = computed<BookingV2MissingRequirement[]>(() => {
        const issues: BookingV2MissingRequirement[] = [];

        const { from, to, openEnd } = this.facade.dateRange();
        if (!from || (!openEnd && !to)) {
            issues.push({ labelKey: 'bookingV2.validation.dateRange', step: 'date' });
        }

        const boat = this.facade.selectedBoat();
        if (!boat || boat.origin === 'manual') {
            issues.push({ labelKey: 'bookingV2.validation.boat', step: 'berth' });
        }

        // A berth occupancy with an incomplete/inverted window would be saved with
        // a wrong period — block Save and point at the berth step (mirrors the
        // legacy per-assignment date validators).
        if (this.facade.berthOccupanciesInvalid()) {
            issues.push({ labelKey: 'bookingV2.validation.berthOccupancy', step: 'berth' });
        }

        if (this.facade.selectedPerson() === null) {
            issues.push({ labelKey: 'bookingV2.validation.person', step: 'person' });
        }

        if (this.hasIncompleteSubscription()) {
            issues.push({ labelKey: 'bookingV2.validation.subscription', step: 'advanced' });
        }

        // Reservation: the confirm-by deadline (→ reservedUntil) is required, and a
        // required deposit needs a positive amount + tax rate, or the deposit
        // position is silently dropped. Gated here so Save (not just Next) blocks.
        if (this.facade.reservationValidUntilMissing()) {
            issues.push({ labelKey: 'bookingV2.validation.reservationValidUntil', step: 'date' });
        }
        if (this.facade.depositIncomplete()) {
            issues.push({ labelKey: 'bookingV2.validation.deposit', step: 'date' });
        }

        // A relation or a dynamic service references the season but none is chosen →
        // the price would silently fall back. The picker lives in the person step.
        if (this.relationPricing.seasonMissing()) {
            issues.push({ labelKey: 'bookingV2.validation.season', step: 'person' });
        }

        return issues;
    });

    // Single source of truth: savable exactly when nothing is missing.
    readonly canCommit = computed<boolean>(() => this.missingRequirements().length === 0);

    // `sendConfirmation` only applies to a new booking (create) — the backend
    // honors `?sendConfirmation` on POST, not PUT (mirrors the legacy dialog).
    commit(sendConfirmation = false): Observable<BcmBooking> {
        const dto = buildBookingV2CommitDto(this.snapshot());

        const bookingId = this.facade.bookingId();
        const isEdit = bookingId != null;

        this.saving.set(true);

        // Body is typed Partial<BcmBooking> but both endpoints consume the richer
        // BcmBookingDto; PUT runs the same nested pipeline as POST.
        const request$ = isEdit
            ? this.bookingsApi.update(bookingId, dto as unknown as Partial<BcmBooking>)
            : this.bookingsApi.add(
                dto as unknown as Partial<BcmBooking>,
                new HttpParams().set('sendConfirmation', sendConfirmation),
            );

        return request$.pipe(
            tap({
                next: () => this.notifications.showSuccess(
                    TolgeeService.translate(this.savedNotificationKey(isEdit)),
                ),
                error: () => this.notifications.showError(
                    TolgeeService.translate('bookingV2.notifications.saveError'),
                ),
            }),
            finalize(() => this.saving.set(false)),
        );
    }

    private savedNotificationKey(isEdit: boolean): string {
        const isReservation = this.facade.bookingType() === 'reservation';
        if (isEdit) {
            return isReservation
                ? 'bookingV2.notifications.reservationUpdated'
                : 'bookingV2.notifications.bookingUpdated';
        }
        return isReservation
            ? 'bookingV2.notifications.reservationSaved'
            : 'bookingV2.notifications.bookingSaved';
    }

    // Reservation down payment → the deposit input the mapper turns into one
    // ad-hoc position. Only when a reservation requires it and the amount is set;
    // create-only (the deposit card is hidden on edit). Vesting = stay period.
    private depositInput(): BookingV2DepositInput | null {
        if (this.facade.bookingType() !== 'reservation' || !this.facade.depositRequired()) {
            return null;
        }
        const amount = this.facade.depositAmount() ?? 0;
        const taxRateId = this.facade.depositTaxRateId();
        if (!(amount > 0) || taxRateId == null) {
            return null;
        }
        const range = this.facade.dateRange();
        return {
            amount,
            taxRateId,
            title: TolgeeService.translate(RESERVATION_DEPOSIT_TITLE_KEY),
            vestingFrom: range.from,
            vestingUntil: range.openEnd ? null : range.to,
        };
    }

    private snapshot(): BookingV2CommitSnapshot {
        return {
            bookingId: this.facade.bookingId(),
            statusId: this.facade.bookingStatusId(),
            baseline: this.facade.baseline(),
            bookingType: this.facade.bookingType(),
            dateRange: this.facade.dateRange(),
            defaultTimeUnit: this.facade.defaultTimeUnit(),
            occupancies: this.facade.berthOccupancies(),
            boat: this.facade.selectedBoat(),
            person: this.facade.selectedPerson(),
            relation: this.facade.selectedRelation(),
            relationLineOverrides: this.facade.effectiveRelationLineOverrides(),
            winterStorageId: this.facade.selectedWinterStorageId(),
            notes: this.facade.bookingNotes(),
            reservationValidUntil: this.facade.reservationValidUntil(),
            reservationText: this.facade.reservationText(),
            deposit: this.depositInput(),
            travelers: this.facade.travelers(),
            services: this.facade.services(),
            electricMeters: this.facade.electricMeterAssignments(),
            keys: this.facade.keys(),
            documents: this.facade.documents(),
            selectedOrderId: this.facade.selectedOrderId(),
        };
    }
}
